<template>
  <span :id="'seat-' + info.number" class="d-inline-block" tabindex="0">
    <button
      v-b-hover="handleHover"
      role="button"
      class="map-block seat"
      :disabled="availability !== 'available'"
      @click="handleSeatSelection"
    >
      <seat-content
        :seat-class="seatClass"
        :is-selected="isSelected"
        :is-blocked="isBlocked"
        :is-available="isAvailable"
        :is-suitable-for-infant="isSuitableForInfant"
        :is-hovered="isHovered"
        :price="price"
        :currency-symbol="currencySymbol"
        :passenger="passenger"
        :show-suitable-for-infant="showSuitableForInfant"
      />
    </button>
    <seat-popover
      :seat-number="info.number"
      :is-available="isAvailable"
      :is-premium="isPremium"
      :is-selected="isSelected"
      :is-partially-available="isPartiallyAvailable"
      :passenger-name="passengerName"
      :availability="availability"
      :price="price"
      :shown-characteristics="shownCharacteristics"
    />
  </span>
</template>

<script>
  import { traveler_name } from '@liveflight/utilities';
  import SeatContent from './seat-content.vue';
  import SeatPopover from './seat-popover.vue';

  export default {
    name: 'seat',
    components: {
      SeatContent,
      SeatPopover,
    },
    props: {
      info: Object,
      passenger: Object,
      handleSeatSelection: Function,
      showSuitableForInfant: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isHovered: false,
      };
    },
    computed: {
      currentTravelerChoosing() {
        return this.info.currentTravelerChoosing;
      },
      seatClass() {
        if (this.isSelected) {
          return this.isPremium ? 'seat-premium-selected' : 'seat-selected';
        }

        if (this.isAvailable) {
          if (this.isPremium) {
            return 'seat-premium';
          }
          if (this.isPartiallyAvailable) {
            return 'seat-partially-available';
          }
        }
        return `seat-${this.availability}`;
      },
      /**
       * Normalized per-traveler view of this seat. Prefers the
       * pricingByTraveler map from build_deck_seatmap (O(1) keyed by
       * travelerId), then the legend's currentTravelerPricing shortcut,
       * then the first entry for provider fallbacks, then {}.
       */
      travelerPricing() {
        if (!this.info) {
          return {};
        }
        const byTraveler = this.info.pricingByTraveler;
        if (
          byTraveler &&
          this.currentTravelerChoosing &&
          byTraveler[this.currentTravelerChoosing]
        ) {
          return byTraveler[this.currentTravelerChoosing];
        }
        if (this.info.currentTravelerPricing) {
          return this.info.currentTravelerPricing;
        }
        if (byTraveler) {
          const firstKey = Object.keys(byTraveler)[0];
          if (firstKey) return byTraveler[firstKey];
        }
        return {};
      },
      availability() {
        return String(this.travelerPricing.availability || '').toLowerCase();
      },
      price() {
        const { price } = this.travelerPricing;
        if (!price || !price.grandTotal) {
          return 0;
        }
        return Number(price.grandTotal);
      },
      currencySymbol() {
        const { price } = this.travelerPricing;
        if (!price) {
          return '';
        }
        return price.currencySymbol || '';
      },
      isPartiallyAvailable() {
        return this.info.isPartiallyAvailable;
      },
      isSelected() {
        return !!this.passenger;
      },
      isSuitableForInfant() {
        return !!this.info.isSuitableForInfant;
      },
      isAvailable() {
        return this.availability === 'available';
      },
      isBlocked() {
        return this.availability === 'blocked';
      },
      isPremium() {
        return !!this.info.isPremium;
      },
      passengerName() {
        return traveler_name(this.passenger);
      },
      shownCharacteristics() {
        return this.info.displayCharacteristics || [];
      },
    },
    methods: {
      handleHover(hovered) {
        this.isHovered = hovered;
      },
    },
  };
</script>

<style lang="scss">
  .seat {
    padding: 0;
    background-color: $light-gray-blue;
    border-color: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .seat-component {
    font-weight: $vb-font-weight-semibold;
    color: $available-seat-border;
    border: 2px solid $available-seat-border;
  }

  .seat-occupied,
  .seat-blocked {
    opacity: 30%;
  }
  .seat-selected {
    background-color: $light-blue;
    color: var(--primary);
    border: 2px solid var(--primary);
  }

  .seat-premium {
    color: $premium-seat-border;
    border: 2px solid $premium-seat-border;
    background-color: $premium-seat-background;
  }

  .seat-premium-selected {
    color: $selected-premium-seat-border;
    border: 2px solid $selected-premium-seat-border;
    background-color: $selected-premium-seat-background;
  }

  .seat-partially-available {
    color: $partially-available-seat-border;
    border: 2px solid $partially-available-seat-border;
    background-color: $partially-available-seat-background;
  }

  .seat-info {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    max-width: 100%;
    min-width: 100%;
    max-height: 100%;
    min-height: 100%;
  }
</style>
